// NB: This file is in @kapitalist/contract. It is used by both @kapitalist/frontend and
// @kapitalist/backend. Changes here will require work on both sides.
//
// Purely presentational data — star ratings, play labels, board ordering — stays in
// the frontend.

import {unreachableCase} from '@kapitalist/utils/exhaustive'
import type {Range} from '@kapitalist/utils/types'
import type {
  TCard,
  TCardData,
  TDualPropertyCard,
  TResidenceCard,
  TStandardPropertyCard,
  TUniversalRentCard,
} from './types/cards'
import {
  EIGHT_M,
  FIVE_M,
  FOUR_M,
  ONE_M,
  SEVEN_M,
  SIX_M,
  TEN_M,
  THREE_M,
  type TMillions,
  TWO_M,
  ZERO_M,
} from './types/millions'
import {MillionsMath} from './utils/millions/math'

export const MAX_NUMBER_OF_PLAYERS = 8
export const MIN_NUMBER_OF_PLAYERS = 2

// Range is half-open, hence the `| MAX`.
export type TValidNumberOfPlayers =
  | Range<typeof MIN_NUMBER_OF_PLAYERS, typeof MAX_NUMBER_OF_PLAYERS>
  | typeof MAX_NUMBER_OF_PLAYERS

export const MAX_CARDS_IN_HAND = 7

export const CARDS_DRAWN_PER_TURN = 2
export const CARDS_DRAWN_EMPTY_HAND = 5

export const CARDS_DEALT_PER_PLAYER = 5
// The first player gets extra so they don't have to separately draw on their opening turn.
export const CARDS_DEALT_FIRST_PLAYER = CARDS_DEALT_PER_PLAYER + CARDS_DRAWN_PER_TURN

// Separate from CARDS_DRAWN_PER_TURN: it matches today, but it's a different rule and
// free to diverge.
export const CARDS_DRAWN_PASS_GO = 2

export const SETS_TO_WIN = 3

export type TRuleset = {
  // oneEachUnordered: can have a house, a hotel, or both
  // oneEachOrdered: can have a house, or a house and hotel.
  // oneTotal: can only have either a house or a hotel, but not both
  // unrestricted: can have N houses, N hotels, or M houses and N hotels
  residenceStacking:
    | 'oneEachUnordered'
    | 'oneEachOrdered'
    | 'oneTotal'
    | 'unrestricted'
  residencesOnUtilitiesAndRailroads: boolean

  /*
   other possible / fun rules we could support, for later reference/pondering.

   // bottomOfDeck would eliminate shuffling, meaning card counting recycled cards
   // would be possible. This would probably be bad for gameplay - JustSayNos and
   // associated high-value action cards would be grouped for re-drawing.
   // Current code assumes `middleOfTable` behavior.
   playedCardsGoTo: 'middleOfTable' | 'bottomOfDeck'

   // meta-game concept, where you can say "I promise I won't win on my next turn",
   // but actually play all the winning cards such that you should have won.
   // Thus you are "zero cards from winning" but the game doesn't end.
   // Current code assumes the first player to meet the win condition ends the game
   // immediately and offers no choice in the matter.
   zeroCardsAway: 'instantWin' | 'allowed'

   // Nit-pick of real-world behavior that could go either way.
   // If you play a Pass Go and there are not enough cards in the deck (0 or 1)
   // to satisfy the draw, should that reshuffle-triggering Pass Go card be included
   // in said reshuffle?
   // Current code shuffles it in.
   cardTriggeringReshuffle: 'reshuffledAlso' | 'leftAsOnlyPlayedCard'

   // With wild cards, it's possible to have more than one set of the same color.
   // Do you need 3 distinct colors to win, or just 3 complete sets?
   // Current code requires distinct colors.
   multipleSetsOfSameColorCanWin: false | true
  */
}

export const defaultRuleset: TRuleset = {
  residenceStacking: 'oneEachUnordered',
  residencesOnUtilitiesAndRailroads: false,
}

export const cardsPerSet: Record<TStandardPropertyCard, 2 | 3 | 4> = {
  darkBlue: 2,
  green: 3,
  yellow: 3,
  red: 3,
  orange: 3,
  pink: 3,
  lightBlue: 3,
  brown: 2,
  railroad: 4,
  utility: 2,
}

// Rent charged for owning `count` properties of a color (index 0 => no rent).
export const rentValue = (
  color: TStandardPropertyCard,
  count: number
): TMillions => {
  let values: TMillions[]
  switch (color) {
    case 'darkBlue':
      values = [THREE_M, EIGHT_M]
      break
    case 'green':
      values = [TWO_M, FOUR_M, SEVEN_M]
      break
    case 'yellow':
      values = [TWO_M, FOUR_M, SIX_M]
      break
    case 'red':
      values = [TWO_M, THREE_M, SIX_M]
      break
    case 'orange':
      values = [ONE_M, THREE_M, FIVE_M]
      break
    case 'pink':
      values = [ONE_M, TWO_M, FOUR_M]
      break
    case 'lightBlue':
      values = [ONE_M, TWO_M, THREE_M]
      break
    case 'brown':
      values = [ONE_M, TWO_M]
      break
    case 'railroad':
      values = [ONE_M, TWO_M, THREE_M, FOUR_M]
      break
    case 'utility':
      values = [ONE_M, TWO_M]
      break
    default:
      return unreachableCase(color)
  }

  const table = [ZERO_M, ...values]
  return count >= table.length ? table[table.length - 1] : table[count]
}

// Bonus rent added by a residence on a complete set.
export const residenceRentValue = (residence: TResidenceCard): TMillions => {
  switch (residence) {
    case 'house':
      return THREE_M
    case 'hotel':
      return FOUR_M
    default:
      return unreachableCase(residence)
  }
}

export const debtCollectorCost = FIVE_M
export const birthdayCost = TWO_M

/**
 * Cash value of every card that can be liquidated.
 *
 * Cards with no cash value are absent from this map - see `anyCardValue`
 * if you want those included.
 */
export const valuableCardValue: Record<
  Exclude<TCard, 'propertyWildCard'>,
  TMillions
> = {
  darkBlue: FOUR_M,
  green: FOUR_M,
  yellow: THREE_M,
  red: THREE_M,
  orange: TWO_M,
  pink: TWO_M,
  lightBlue: ONE_M,
  brown: ONE_M,
  railroad: TWO_M,
  utility: TWO_M,

  wildDarkBlueAndGreen: FOUR_M,
  wildGreenAndRailroad: FOUR_M,
  wildLightBlueAndRailroad: FOUR_M,
  wildRedAndYellow: THREE_M,
  wildUtilityAndRailroad: TWO_M,
  wildPinkAndOrange: TWO_M,
  wildLightBlueAndBrown: ONE_M,

  directionalRent: THREE_M,
  rentDarkBlueGreen: ONE_M,
  rentLightBlueBrown: ONE_M,
  rentPinkOrange: ONE_M,
  rentUtilityRailroad: ONE_M,
  rentRedYellow: ONE_M,

  dealBreaker: FIVE_M,
  justSayNo: FOUR_M,
  slyDeal: THREE_M,
  forcedDeal: THREE_M,
  debtCollector: THREE_M,
  birthday: TWO_M,
  doubleTheRent: ONE_M,
  passGo: ONE_M,

  house: THREE_M,
  hotel: FOUR_M,

  tenMil: TEN_M,
  fiveMil: FIVE_M,
  fourMil: FOUR_M,
  threeMil: THREE_M,
  twoMil: TWO_M,
  oneMil: ONE_M,
}

/** Unlike valuableCardValue, this includes no-cash-value cards. */
export const anyCardValue: Record<TCard, TMillions> = {
  ...valuableCardValue,
  propertyWildCard: ZERO_M,
}

export const valueOfCards = (cards: TCardData[]): TMillions =>
  MillionsMath.sum(cards.map(c => anyCardValue[c.type]))

// How many of each card the full deck contains.
export const cardsInDeck: Record<TCard, number> = {
  darkBlue: 2,
  green: 3,
  yellow: 3,
  red: 3,
  orange: 3,
  pink: 3,
  lightBlue: 3,
  brown: 2,
  railroad: 4,
  utility: 2,

  wildDarkBlueAndGreen: 1,
  wildGreenAndRailroad: 1,
  wildLightBlueAndRailroad: 1,
  wildRedAndYellow: 2,
  wildUtilityAndRailroad: 1,
  wildPinkAndOrange: 2,
  wildLightBlueAndBrown: 1,

  propertyWildCard: 2,

  directionalRent: 3,
  rentDarkBlueGreen: 2,
  rentLightBlueBrown: 2,
  rentPinkOrange: 2,
  rentUtilityRailroad: 2,
  rentRedYellow: 2,

  dealBreaker: 2,
  justSayNo: 3,
  slyDeal: 3,
  forcedDeal: 3,
  debtCollector: 3,
  birthday: 3,
  doubleTheRent: 2,
  passGo: 10,

  house: 3,
  hotel: 2,

  tenMil: 1,
  fiveMil: 2,
  fourMil: 3,
  threeMil: 3,
  twoMil: 5,
  oneMil: 6,
}

export const totalCardsInDeck = Object.values(cardsInDeck).reduce((a, b) => a + b, 0)

export type TCardClass =
  | 'cash'
  | 'residence'
  | 'action'
  | 'rent'
  | 'standardProperty'
  | 'dualProperty'
  | 'propertyWildCard'

export const cardClass: Record<TCard, TCardClass> = {
  darkBlue: 'standardProperty',
  green: 'standardProperty',
  yellow: 'standardProperty',
  red: 'standardProperty',
  orange: 'standardProperty',
  pink: 'standardProperty',
  lightBlue: 'standardProperty',
  brown: 'standardProperty',
  railroad: 'standardProperty',
  utility: 'standardProperty',

  wildDarkBlueAndGreen: 'dualProperty',
  wildGreenAndRailroad: 'dualProperty',
  wildUtilityAndRailroad: 'dualProperty',
  wildLightBlueAndRailroad: 'dualProperty',
  wildLightBlueAndBrown: 'dualProperty',
  wildPinkAndOrange: 'dualProperty',
  wildRedAndYellow: 'dualProperty',

  directionalRent: 'rent',
  rentDarkBlueGreen: 'rent',
  rentLightBlueBrown: 'rent',
  rentPinkOrange: 'rent',
  rentUtilityRailroad: 'rent',
  rentRedYellow: 'rent',

  dealBreaker: 'action',
  debtCollector: 'action',
  doubleTheRent: 'action',
  slyDeal: 'action',
  forcedDeal: 'action',
  birthday: 'action',
  justSayNo: 'action',
  passGo: 'action',

  house: 'residence',
  hotel: 'residence',

  tenMil: 'cash',
  fiveMil: 'cash',
  fourMil: 'cash',
  threeMil: 'cash',
  twoMil: 'cash',
  oneMil: 'cash',

  propertyWildCard: 'propertyWildCard',
}

export const isDualPropertyType = (
  type: TDualPropertyCard | TStandardPropertyCard
): type is TDualPropertyCard => {
  return cardClass[type] === 'dualProperty'
}

// The two colors a dual-property card can be played as.
export const standardPropertyTypesForDualPropertyCard = (
  type: TDualPropertyCard
): [TStandardPropertyCard, TStandardPropertyCard] => {
  switch (type) {
    case 'wildDarkBlueAndGreen':
      return ['darkBlue', 'green']
    case 'wildGreenAndRailroad':
      return ['green', 'railroad']
    case 'wildLightBlueAndBrown':
      return ['lightBlue', 'brown']
    case 'wildLightBlueAndRailroad':
      return ['lightBlue', 'railroad']
    case 'wildPinkAndOrange':
      return ['pink', 'orange']
    case 'wildRedAndYellow':
      return ['red', 'yellow']
    case 'wildUtilityAndRailroad':
      return ['utility', 'railroad']
    default:
      return unreachableCase(type)
  }
}

// The two colors a universal (directionless) rent card can charge for.
export const standardPropertyTypesForUniversalRentCard = (
  type: TUniversalRentCard
): [TStandardPropertyCard, TStandardPropertyCard] => {
  switch (type) {
    case 'rentDarkBlueGreen':
      return ['darkBlue', 'green']
    case 'rentLightBlueBrown':
      return ['lightBlue', 'brown']
    case 'rentPinkOrange':
      return ['pink', 'orange']
    case 'rentRedYellow':
      return ['red', 'yellow']
    case 'rentUtilityRailroad':
      return ['utility', 'railroad']
    default:
      return unreachableCase(type)
  }
}
